import { Helmet } from "react-helmet-async";
import { SITE_URL } from "@/config/site";
import { useTranslation } from "react-i18next";
import { LOCALIZED_PATHS } from "@/lib/localizedPaths";

const SITE = SITE_URL;
// French canonical stays at the un-prefixed root; other locales are prefixed.
// "/" → "" so the home URL is /en, not /en/.
const localeUrl = (lng, path) =>
  lng === "fr" ? `${SITE}${path}` : `${SITE}/${lng}${path === "/" ? "" : path}`;

// 15 locales the app actively serves via i18next + sitemap.xml. Stronger
// Bot is a SPA — the URL stays the same regardless of language (i18next
// switches content client-side from the LanguageSelector / browser locale)
// — so each hreflang href points at the same canonical URL, which Google
// explicitly supports for content-negotiated SPAs.
//
// Order mirrors public/locales/ alphabetically; x-default points at the
// canonical URL too so crawlers without a locale preference land on FR.
const HREFLANG_LOCALES = [
  "ar",
  "de",
  "en",
  "es",
  "fr",
  "hi",
  "it",
  "ja",
  "ko",
  "nl",
  "pl",
  "pt",
  "ru",
  "tr",
  "zh",
];

// og:locale uses underscore-form. Mapped to default region per language
// (e.g. en → en_US, pt → pt_BR). Used for OpenGraph + Twitter cards.
const OG_LOCALE_MAP = {
  fr: "fr_FR",
  en: "en_US",
  es: "es_ES",
  de: "de_DE",
  it: "it_IT",
  pt: "pt_BR",
  tr: "tr_TR",
  nl: "nl_NL",
  pl: "pl_PL",
  ru: "ru_RU",
  ja: "ja_JP",
  zh: "zh_CN",
  ko: "ko_KR",
  hi: "hi_IN",
  ar: "ar_AR",
};

const SEO = ({ title, description, canonical, ogImage, jsonLd }) => {
  const { i18n } = useTranslation();
  const siteName = "Stronger Bot";
  const siteUrl = SITE;
  const defaultDesc =
    "Stronger Bot - Votre assistant IA pour la productivité. Chat, documents, images, voix et plus encore.";
  const defaultImage = `${siteUrl}/icons/og-image.png`;

  const fullTitle = title
    ? `${title} | ${siteName}`
    : `${siteName} - AI Assistant`;
  const metaDesc = description || defaultDesc;
  const ogImg = ogImage || defaultImage;

  // Per-locale SEO for pages that actually have localized URLs; otherwise keep
  // the historical single self-referential canonical + hreflang.
  const basePath = canonical || "/";
  const isLocalized = LOCALIZED_PATHS.has(basePath);
  const lng = (i18n.language || "fr").split("-")[0];
  const currentLng = isLocalized ? lng : "fr";
  const canonicalUrl = isLocalized
    ? localeUrl(currentLng, basePath)
    : canonical
    ? `${siteUrl}${canonical}`
    : siteUrl;
  const hrefFor = (lang) =>
    isLocalized ? localeUrl(lang, basePath) : canonicalUrl;
  const ogLocaleCurrent = OG_LOCALE_MAP[currentLng] || "fr_FR";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang alternates + x-default. For localized pages these point at
          the real per-locale URLs (/en/…, /es/…, French at root); for the rest
          they stay self-referential (one URL, content switches client-side). */}
      {HREFLANG_LOCALES.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={hrefFor(lang)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={hrefFor("fr")} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImg} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={ogImg} />
      <meta property="og:locale" content={ogLocaleCurrent} />
      {/* og:locale:alternate — every locale we support, lets Facebook /
          LinkedIn pick the matching translation when the user's prefs
          align with one we serve. */}
      {Object.entries(OG_LOCALE_MAP)
        .filter(([lang]) => lang !== "fr")
        .map(([lang, ogLocale]) => (
          <meta key={lang} property="og:locale:alternate" content={ogLocale} />
        ))}
      {/* Pinterest Rich Pins */}
      <meta
        property="og:see_also"
        content="https://www.pinterest.com/strongerbot/"
      />
      <meta name="pinterest-rich-pin" content="true" />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
